import {
  Client,
  createUserProjectSetting,
  CreateUserProjectSettingResponse,
  createUserSetting,
  CreateUserSettingResponse,
  deleteUserProjectSetting,
  deleteUserSetting,
  getUserProjectSetting,
  GetUserProjectSettingResponse,
  getUserSetting,
  GetUserSettingResponse,
  listUserProjectSettings,
  ListUserProjectSettingsResponse,
  listUserSettings,
  ListUserSettingsResponse,
  updateUserProjectSetting,
  UpdateUserProjectSettingResponse,
  updateUserSetting,
  UpdateUserSettingResponse,
  CreateSetting,
  UpdateSetting,
} from '../generated/index';
import { useQuery, UseQueryResult } from '@tanstack/solid-query';
import { returnOrThrow } from '@trimble/http-utils';

export class SettingsApiService {
  constructor(private readonly client: Client) {}

  //#region USER SETTINGS
  public listUserSettingsQuery(): UseQueryResult<
    ListUserSettingsResponse,
    Error
  > {
    return useQuery(() => ({
      queryKey: ['userSettings'],
      queryFn: async (): Promise<ListUserSettingsResponse> => {
        const response = await listUserSettings({
          client: this.client,
        });
        return returnOrThrow(response, 'Error while listing user settings');
      },
    }));
  }

  public async createUserSetting(
    key: string,
    body: CreateSetting
  ): Promise<CreateUserSettingResponse> {
    const response = await createUserSetting({
      // eslint-disable-next-line @typescript-eslint/naming-convention -- API requires PascalCase TODO: FIX on API side
      query: { Key: key },
      body,
      client: this.client,
    });
    return returnOrThrow(response, 'Error while creating user setting');
  }

  public async getUserSetting(key: string): Promise<GetUserSettingResponse> {
    const response = await getUserSetting({
      path: { key },
      client: this.client,
    });
    return returnOrThrow(response, 'Error while getting user setting');
  }

  public async updateUserSetting(
    key: string,
    body: UpdateSetting
  ): Promise<UpdateUserSettingResponse> {
    const response = await updateUserSetting({
      path: { key },
      body,
      client: this.client,
    });
    return returnOrThrow(response, 'Error while updating user setting');
  }

  public async deleteUserSetting(key: string): Promise<unknown> {
    const response = await deleteUserSetting({
      path: { key },
      client: this.client,
    });
    return returnOrThrow(response, 'Error while deleting user setting');
  }
  //#endregion

  //#region USER SETTINGS PER PROJECT
  public listUserProjectSettingsQuery(
    tenantTrn: string
  ): UseQueryResult<ListUserProjectSettingsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['projectSettings', tenantTrn],
      queryFn: async (): Promise<ListUserProjectSettingsResponse> => {
        const response = await listUserProjectSettings({
          client: this.client,
          path: { tenantTrn },
        });
        return returnOrThrow(response, 'Error while listing project settings');
      },
      enabled: !!tenantTrn,
    }));
  }

  public async createUserProjectSetting(
    tenantTrn: string,
    key: string,
    body: CreateSetting
  ): Promise<CreateUserProjectSettingResponse> {
    const response = await createUserProjectSetting({
      path: { tenantTrn },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- API requires PascalCase TODO: FIX on API side
      query: { Key: key },
      body,
      client: this.client,
    });
    return returnOrThrow(response, 'Error while creating project setting');
  }

  public async getUserProjectSetting(
    tenantTrn: string,
    key: string
  ): Promise<GetUserProjectSettingResponse> {
    const response = await getUserProjectSetting({
      path: { tenantTrn, key },
      client: this.client,
    });
    return returnOrThrow(response, 'Error while getting project setting');
  }

  public getUserProjectSettingQuery(
    tenantTrn: string,
    key: string
  ): UseQueryResult<GetUserProjectSettingResponse, Error> {
    return useQuery(() => ({
      queryKey: ['userProjectSetting', tenantTrn, key],
      queryFn: async (): Promise<GetUserProjectSettingResponse> => {
        const response = await getUserProjectSetting({
          path: { tenantTrn, key },
          client: this.client,
        });
        return returnOrThrow(response, 'Error while getting project setting');
      },
      enabled: !!tenantTrn,
    }));
  }

  public async updateUserProjectSetting(
    tenantTrn: string,
    key: string,
    body: UpdateSetting
  ): Promise<UpdateUserProjectSettingResponse> {
    const response = await updateUserProjectSetting({
      path: { tenantTrn, key },
      body,
      client: this.client,
    });
    return returnOrThrow(response, 'Error while updating project setting');
  }

  public async deleteUserProjectSetting(
    tenantTrn: string,
    key: string
  ): Promise<unknown> {
    const response = await deleteUserProjectSetting({
      path: { tenantTrn, key },
      client: this.client,
    });
    return returnOrThrow(response, 'Error while deleting project setting');
  }
  //#endregion
}
